/**
 * AddReportCtrl controller
 * Add/Edit Reports
 */

import { REPORT_TYPES, FILTER_NAMES_BY_TYPES, REPORT_FIELDS_LIST } from 'app/core/constants'
import { ascBy, lowerCaseProperty } from 'app/helpers/object'
import { keyValue } from 'app/helpers/entity'
import * as _ from 'lodash-es'

/**
 * AddReportCtrl controller
 *
 * @class AddReportCtrl
 * @memberof mwApp/reports
 * @see mwApp/reports
 *
 * @ngdoc controller
 * @name mwApp/reports.controller:AddReportCtrl
 */
/* @ngInject */
export default function AddReportCtrl ($scope, $location, $http, alertLiteSvc, ReportSeed, Network, Filter, commonData) {
  var vm = this

  vm.statuses = {
    TOA: 'TOA Pending',
    ACTIVE: 'Active',
    EXPIRED: 'Expired'
  }

  /**
   * Watch filter list changes.
   */
  $scope.$watch(() => vm.report.filterList, (n, o) => {
    if (n.length && n.length === o.length) {
      const report = angular.copy(vm.report)

      renderReport(report, response => {
        const data = response.data

        vm.selectedOption = data.reportOption.selectedOption
        vm.filterOption = data.reportOption.filterOption
        vm.permissibleFields = getPermissibleFields(data.reportOption)

        vm.report = report
      }, true)
    }
  }, true)

  /**
   * Report state model
   *
   * @memberof mwApp/reports.controller:AddReportCtrl
   * @type {Object}
   */
  vm.report = new ReportSeed()

  /**
   * Packages list
   *
   * @memberof mwApp/reports.controller:AddReportCtrl
   * @type {Array}
   */
  vm.packages = []

  /**
   * Packages list
   *
   * @memberof mwApp/reports.controller:AddReportCtrl
   * @type {Array}
   */
  vm.permissibleFields = []

  /**
   * Report types object
   *
   * @memberof mwApp/reports.controller:AddReportCtrl
   * @type {Array}
   */
  vm.reportTypes = []

  /**
   * Filter names by type
   *
   * @memberof mwApp/reports.controller:AddReportCtrl
   * @type {Object}
   */
  vm.filterNames = FILTER_NAMES_BY_TYPES

  /**
   * Reports fields
   *
   * @memberof mwApp/reports.controller:AddReportCtrl
   * @type {Array}
   */
  vm.reportsFields = []

  /**
   * Selected filters
   *
   * @memberof mwApp/reports.controller:AddReportCtrl
   * @type {Array}
   */
  vm.selectedOption = []

  /**
   * Filtered filters
   *
   * @memberof mwApp/reports.controller:AddReportCtrl
   * @type {Array}
   */
  vm.filterOption = []

  /**
   *
   * @type {boolean}
   */
  vm.disableStatusRange = true

  /**
   * Load extra data [packages, type]
   */
  ReportSeed.load('api/reports/extra', {
    reportTypes: [String],
    networks: ['Network']
  }).then(
    data => {
      vm.packages = data.networks.map(keyValue())
      vm.packages.sort(ascBy(lowerCaseProperty('value')))
      vm.packages = [{ key: null, value: 'All Organizations' }].concat(vm.packages)

      vm.reportTypes = data.reportTypes
        .filter(type => REPORT_TYPES[type])
        .map(key => ({ key, value: REPORT_TYPES[key] })
        )
    })
    .then(() => {
      const defaultType = 'SUBSCRIBER'
      if (vm.reportTypes.find(type => type.key === defaultType)) {
        vm.report.reportType = defaultType
      }
    })

  $scope.$watch('vm.report.reportType', () => {
    if (vm.report.reportType) { vm.changeReportType() }
  })

  /**
   * Generate report
   *
   * @memberof mwApp/reports.controller:AddReportCtrl
   */
  vm.generateReport = function () {
    vm.setDisable()
    vm.report
      ._save(
        report => {
          if (!report.fieldList.length) {
            report.fieldList = vm.reportsFields.map(r => r.key)
          }
        },
        'api/reports'
      )
      .then(function (response) {
        $location.hash('generated_reports')

        commonData.data.update('lastReport', response.data.id)

        vm.location.reload()
      })
      .finally(vm.unsetDisable)
  }

  /**
   * Save reports
   *
   * @memberof mwApp/reports.controller:AddReportCtrl
   */
  vm.submit = function () {
    vm.setDisable()
    vm.report
      ._save(
        report => {
          if (!report.fieldList.length) {
            report.fieldList = vm.reportsFields.map(r => r.key)
          }
        },
        'api/reporttemplates'
      )
      .then(function (response) {
        $location.hash('reports_list')

        commonData.data.update('lastTemplate', response.data.id)

        vm.location.reload()
      })
      .finally(vm.unsetDisable)
  }

  /**
   * Change report type
   *
   * @memberof mwApp/reports.controller:AddReportCtrl
   */
  vm.changeReportType = function () {
    vm.report.filterList = []
    vm.report.fieldList = []
    vm.reportsFields = []
    vm.permissibleFields = []

    if (!vm.report.reportType) {
      return false
    }

    renderReport(vm.report, response => {
      const data = response.data

      vm.filterOption = data.reportOption.filterOption
      vm.reportsFields = data.reportOption.fieldList.map(function (k) {
        return {
          key: k,
          value: REPORT_FIELDS_LIST[k]
        }
      })

      vm.permissibleFields = data.reportOption.filterOption.map(m => m.reportElementType)
    })
  }

  /**
   * Add filter field
   *
   * @memberof mwApp/reports.controller:AddReportCtrl
   */
  vm.addFilter = function (filterType) {
    const report = addFilterByType(angular.copy(vm.report), filterType)

    renderReport(report, response => {
      const data = response.data

      vm.selectedOption = data.reportOption.selectedOption
      vm.filterOption = data.reportOption.filterOption
      vm.permissibleFields = getPermissibleFields(data.reportOption)

      vm.report = report
    })
  }

  /**
   * Remove filter field
   *
   * @memberof mwApp/reports.controller:AddReportCtrl
   */
  vm.removeFilter = function (filterType) {
    const report = removeFiltersByType(Object.assign(vm.report), filterType)

    renderReport(report, response => {
      const data = response.data

      vm.selectedOption = data.reportOption.selectedOption
      vm.filterOption = data.reportOption.filterOption
      vm.permissibleFields = getPermissibleFields(data.reportOption)

      vm.report = report
    })
  }

  /**
   * Get filter options.
   *
   * @param filterType
   * @returns {Object}
   */
  vm.generateFilterState = function (filterType) {
    const actualFilterOption = vm.filterOption.find(function (f) {
      return f.reportElementType === filterType
    }) || {}

    const actualSelectedOption = vm.selectedOption.find(function (s) {
      return s.abstractFilter.reportElementType === filterType
    }) || {}

    return Object.assign(actualFilterOption, actualSelectedOption)
  }

  /**
   * Set focus id for form filters.
   *
   * @param elName
   */
  vm.setFocus = function (elName) {
    vm.focusedElementName = elName
  }

  /**
   * Add filter by type
   *
   * @param report
   * @param type
   * @returns {boolean|undefined}
   */
  function addFilterByType (report, type) {
    const index = vm.permissibleFields.indexOf(type)
    const filterFields = {}

    if (index !== -1) {
      filterFields.reportElementType = type

      // Note: for only "status" ??
      if (vm.filterOption.length) {
        const option = vm.filterOption.find(function (f) {
          return f.reportElementType === type && f.abstractFilterValues.length
        })

        if (option) {
          filterFields.status = option.abstractFilterValues[0]
        }
      }

      report.filterList.push(new Filter(filterFields))

      return report
    }
  }

  /**
   * Remove filters by type
   *
   * @param report
   * @param type
   */
  function removeFiltersByType (report, type) {
    report.filterList = vm.report.filterList.filter(function (filter) {
      if (filter.reportElementType === type) {
        vm.permissibleFields.push(type)
      }

      return filter.reportElementType !== type
    })

    return report
  }

  /**
   * Render report values by callback
   *
   * @param report
   * @param callback
   * @param debounce
   */
  function renderReport (report, callback, debounce = false) {
    if (debounce) {
      vm.currentReport = report
      vm.callback = callback
      vm.renderReportDebounced()
    } else {
      $http.post(
        'api/reports/extra',
        report._map()
      ).then(callback)
        .catch(alertLiteSvc.alertError)
    }
  }
  vm.getReport = function () {
    $http.post(
      'api/reports/extra',
      vm.currentReport._map()
    ).then(vm.callback)
      .catch(alertLiteSvc.alertError)
  }

  vm.renderReportDebounced = _.debounce(vm.getReport, 800)
  /**
   *  Filter filterOption and get permissible fields
   * @param reportOption
   * @returns {Array}
   */
  function getPermissibleFields (reportOption) {
    return reportOption.filterOption
      .filter(
        function (f) { return this.indexOf(f.reportElementType) === -1 },
        reportOption.selectedOption.map(s => s.abstractFilter.reportElementType)
      )
      .map(m => m.reportElementType)
  }
}
